import { useEffect, useState } from 'react';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useFormValidation from '../../hooks/useFormValidation';
import {
  createUserSchema,
  updateUserSchema,
  ICreateUserFormData,
} from '../../schemas/userSchemas';
import {
  useCreateUser,
  useUpdateUser,
} from '../../hooks/queries/useUserQueries';
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
} from '../../constants/countryCodes';
import { IUser } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const INITIAL_VALUES: ICreateUserFormData = {
  username: '',
  displayName: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  mobileNumber: '',
  password: '',
};

interface IUserFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user?: IUser | null;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-xs text-red-500 mt-1">{message}</p> : null;

const UserFormDialog = ({ isOpen, onClose, user }: IUserFormDialogProps) => {
  const { t } = useTranslation();
  const isEdit = !!user;
  const [showPassword, setShowPassword] = useState(false);
  const schema = (
    isEdit ? updateUserSchema : createUserSchema
  ) as z.ZodSchema<ICreateUserFormData>;
  const { values, errors, handleChange, handleSubmit, reset, isSubmitting } =
    useFormValidation(schema, INITIAL_VALUES);
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  useEffect(() => {
    if (isOpen && user) {
      reset({
        username: user.username,
        displayName: user.displayName,
        countryCode: user.countryCode || DEFAULT_COUNTRY_CODE,
        mobileNumber: user.mobileNumber || '',
        password: '',
      });
    } else if (isOpen) {
      reset();
    }
  }, [isOpen, user]);

  const handleFormSubmit = async () => {
    await handleSubmit(async (data) => {
      if (isEdit) {
        await updateUser.mutateAsync({
          id: user!.id,
          data: {
            displayName: data.displayName,
            mobileNumber: data.mobileNumber || undefined,
            countryCode: data.countryCode || undefined,
            password: data.password || undefined,
          },
        });
      } else {
        await createUser.mutateAsync({
          username: data.username,
          displayName: data.displayName,
          password: data.password,
          mobileNumber: data.mobileNumber || undefined,
          countryCode: data.countryCode || undefined,
        });
      }
      onClose();
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('users.edit_user') : t('users.create_user')}
          </DialogTitle>
          {!isEdit && (
            <DialogDescription>{t('users.create_user_desc')}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.username')}
            </label>
            <Input
              value={values.username}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder={t('users.username_placeholder')}
              disabled={isEdit}
            />
            <FieldError message={errors.username} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('common.display_name')}
            </label>
            <Input
              value={values.displayName}
              onChange={(e) => handleChange('displayName', e.target.value)}
              placeholder={t('users.display_name_placeholder')}
            />
            <FieldError message={errors.displayName} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.mobile_number')}
            </label>
            <div className="flex gap-2">
              <Select
                value={values.countryCode}
                onValueChange={(val) => handleChange('countryCode', val)}
              >
                <SelectTrigger className="w-auto min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(({ code, label }) => (
                    <SelectItem key={code} value={code}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={values.mobileNumber}
                onChange={(e) => handleChange('mobileNumber', e.target.value)}
                placeholder={t('users.mobile_placeholder')}
                className="flex-1"
              />
            </div>
            <FieldError message={errors.mobileNumber || errors.countryCode} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isEdit ? t('users.password_keep') : t('common.password')}
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={t('users.password_placeholder')}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {!isEdit && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('validation.password_hint')}
              </p>
            )}
            <FieldError message={errors.password} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleFormSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? t('common.saving')
              : isEdit
                ? t('common.update')
                : t('users.add_user')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserFormDialog;
